import { Button, Stack } from '@eleveo/components-react';
import { UserAddIcon } from '@eleveo/icons-react';
import arrayMutators from 'final-form-arrays';
import cloneDeep from 'lodash.clonedeep';
import { equals } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { OnChange } from 'react-final-form-listeners';
import { useTranslation } from 'react-i18next';

import { ShiftTemplateForm } from './ShiftTemplateForm';
import { ShiftTemplatesBulkAssignForm } from './ShiftTemplatesBulkAssignForm';
import { formatValues, initializeDayTemplates } from './config';
import { ASSIGN_SHIFT_BUTTON_TAG, SAVE_SHIFT_TEMPLATE_BUTTON_TAG } from './constants';
import {
  ChangeModifiedShiftTypeModal,
  CloningState,
  ShiftTemplateSidebarProps,
  ShiftTemplatesFormValues,
} from './types';
import { duplicateTemplateSettings, getShiftTemplateIndexForStartOfEvenOddWeek, isDayTemplateEmpty } from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import { SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { SidebarForm } from '../../components/LayoutNext/SidebarForm';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  Agent,
  DayTemplate,
  ShiftTemplate,
  ShiftTemplateType,
  useGetShiftTemplatesQuery,
  useGetSupervisedAgentsAllQuery,
} from '../../graphql/components';
import { SortOrder, sortAgentsBy } from '../../types';
import { customSort } from '../../utils/sort';

export const ShiftTemplatesFormSidebar: React.FC<ShiftTemplateSidebarProps> = ({
  handleCancel,
  handleSubmit,
  shiftTemplate,
  setAgentIdsToAssign,
  agentIdsToAssign,
  shiftTemplates,
}) => {
  const { localeObj, userId } = useAuth();

  const { t } = useTranslation();
  const [modal, setModal] = useState<ChangeModifiedShiftTypeModal>({ state: 'ready' });
  const [cloningState, setCloningState] = useState<CloningState>('ready');
  // switch this state to set up different days within week (used for odd/even and specific days types of shift)
  const [currentDayTemplateIndex, setCurrentDayTemplateIndex] = useState(() => {
    // TODO: return 0 when SPECIFIC_DAYS type is used (starting with 1st day of month)
    if (!shiftTemplate || shiftTemplate.type === ShiftTemplateType.AllWorkDays) {
      return 0;
    }
    return getShiftTemplateIndexForStartOfEvenOddWeek(localeObj);
  });

  const { toggleSidebarExpanded, isSidebarExpanded } = useSidebar();

  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [existingShiftTemplates, setExistingShiftTemplates] = useState<ShiftTemplate[]>([]);

  const { data: agentsResult, loading: agentsLoading } = useGetSupervisedAgentsAllQuery({
    fetchPolicy: 'no-cache',
    variables: {
      supervisorId: userId,
    },
  });

  const { data: existingShiftTemplatesResult, loading: shiftTemplatesLoading } = useGetShiftTemplatesQuery({
    fetchPolicy: 'no-cache',
    skip: !!shiftTemplate,
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (cloningState === 'finishing') {
      timeout = setTimeout(() => {
        setCloningState('ready');
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [cloningState]);

  useEffect(() => {
    if (!agentsLoading) {
      const agentsData = agentsResult?.supervisedAgents?.agents ?? [];

      const agentsDataSorted = customSort(agentsData, sortAgentsBy.name, SortOrder.ASCENDING);
      setAgents(agentsDataSorted);
    }
  }, [agentsResult?.supervisedAgents?.agents, agentsLoading]);

  useEffect(() => {
    if (!shiftTemplatesLoading) {
      const shiftTemplatesData = existingShiftTemplatesResult?.shiftTemplates?.shiftTemplates ?? [];

      const shiftTemplatesDataSorted = customSort(shiftTemplatesData, ['name'], SortOrder.ASCENDING);

      setExistingShiftTemplates(shiftTemplatesDataSorted);
    }
  }, [existingShiftTemplatesResult?.shiftTemplates?.shiftTemplates, shiftTemplatesLoading]);

  useEffect(() => {
    /**
     * prevents modal from looping between open and ready states as OnChange handler should not be triggered
     * when type of shift template has been changed due to user's interaction with modal
     * or due to cloning options of selected shift template
     */
    if (modal.state === 'closing') {
      setModal({ state: 'ready' });
    }
  }, [modal.state]);

  const computedInitialValues = useMemo(() => formatValues(shiftTemplate), [shiftTemplate]);

  const handleModalClose = () => {
    if (modal.state === 'open') {
      setModal({ state: 'closing' });
    }
  };

  const changeCurrentDayTemplateIndex = (type?: ShiftTemplateType) => {
    if (type === ShiftTemplateType.EvenOdd) {
      // switch template to first day within even week (0 - monday, 6 - sunday)
      setCurrentDayTemplateIndex(getShiftTemplateIndexForStartOfEvenOddWeek(localeObj));
    } else {
      // applies to both ALL_WORK_DAYS and SPECIFIC_DAYS types
      setCurrentDayTemplateIndex(0);
    }
  };

  const handleModalSubmit = () => {
    if (modal.state === 'open') {
      modal.form.change('type', modal.newType);
      modal.form.change('dayTemplates', initializeDayTemplates(modal.newType));
      modal.form.resetFieldState('dayTemplates');
      changeCurrentDayTemplateIndex(modal.newType);
      setModal({ state: 'closing' });
    }
  };

  return (
    <SidebarForm<ShiftTemplatesFormValues>
      initialValues={computedInitialValues}
      initialValuesEqual={equals}
      mutators={{ ...arrayMutators }}
      onSubmit={handleSubmit}
      onClose={handleCancel}
      onExtend={toggleSidebarExpanded}
      title={t(shiftTemplate ? 'shiftTemplate.update' : 'shiftTemplate.new')}
      name="shift-template-form"
    >
      {({ form, invalid, pristine, values }) => (
        <>
          <SidebarPanel>
            <Stack direction="horizontal" className="w-full">
              <Button
                color="success"
                isDisabled={(pristine && !agentIdsToAssign) || invalid}
                testId={SAVE_SHIFT_TEMPLATE_BUTTON_TAG}
                type="submit"
              >
                {t('layout.action.save')}
              </Button>
              <Button
                aria-expanded={isSidebarExpanded}
                testId={ASSIGN_SHIFT_BUTTON_TAG}
                onClick={toggleSidebarExpanded}
                isSelected={isSidebarExpanded}
                className="mis-auto"
              >
                <UserAddIcon /> {t('person.assignPeople')}
              </Button>
            </Stack>
          </SidebarPanel>
          <SidebarContent>
            <SidebarPart>
              <ShiftTemplateForm
                currentDayTemplateIndex={currentDayTemplateIndex}
                existingShiftTemplates={existingShiftTemplates}
                form={form}
                isCloning={cloningState !== 'ready'}
                isCreate={!shiftTemplate}
                isLoading={shiftTemplatesLoading}
                t={t}
                setCurrentDayTemplateIndex={setCurrentDayTemplateIndex}
              />
            </SidebarPart>
            {isSidebarExpanded && (
              <SidebarPart>
                <ShiftTemplatesBulkAssignForm
                  agentIdsToAssign={agentIdsToAssign}
                  agentsAll={agents}
                  loading={agentsLoading || !agents}
                  setAgentIdsToAssign={setAgentIdsToAssign}
                  shiftTemplateId={shiftTemplate?.id}
                  t={t}
                  shiftTemplates={shiftTemplates}
                  effectiveDate={values.effectiveDate?.toString()}
                />
              </SidebarPart>
            )}
          </SidebarContent>
          <OnChange name="type">
            {(newType, previousType) => {
              // TODO: when adding next shift type, initialValues should be replaced
              if (modal.state === 'ready') {
                if (!newType) {
                  // do not allow to clear type of shift template
                  form.change('type', previousType);
                } else if (values.dayTemplates.filter((dayTemplate) => !isDayTemplateEmpty(dayTemplate)).length) {
                  // reset fields state to previous type so that for is not visually changed until modal is submitted
                  form.change('type', previousType);
                  setModal({ newType, state: 'open', form });
                } else {
                  changeCurrentDayTemplateIndex(newType);
                  form.change('dayTemplates', initializeDayTemplates(newType));
                  // form.resetFieldState('dayTemplates'); // Commented as temporary fix for WFM-4112
                }
              }
            }}
          </OnChange>
          <OnChange name="cloneShiftTemplateId">
            {(shiftTemplateIdToClone) => {
              const previousName = values.name;
              const previousEffectiveDate = values.effectiveDate;

              if (!shiftTemplateIdToClone) {
                const newValues = formatValues();

                // all fields except name and effective date should be reset
                form.batch(() => {
                  form.initialize(newValues);
                  // this change needs be provided separately to not have form in pristine state
                  if (previousName) {
                    form.change('name', previousName);
                  }
                  if (previousEffectiveDate) {
                    form.change('effectiveDate', previousEffectiveDate);
                  }
                });

                return;
              }

              const shiftTemplateToClone: Partial<ShiftTemplate> | undefined = cloneDeep(
                existingShiftTemplates.find(({ id }) => id === shiftTemplateIdToClone)
              );

              if (shiftTemplateToClone) {
                delete shiftTemplateToClone.name;
                delete shiftTemplateToClone.id;
                delete shiftTemplateToClone.state;
                delete shiftTemplateToClone.effectiveDate;

                if (shiftTemplateToClone.dayTemplates?.length) {
                  // we should remove ids from cloned dayTemplate and its inner objects (breaks, scheduleActivities)
                  shiftTemplateToClone.dayTemplates = shiftTemplateToClone.dayTemplates.map((dayTemplate) =>
                    duplicateTemplateSettings({ ...dayTemplate, id: undefined }, dayTemplate)
                  ) as DayTemplate[];
                }

                // to prevent modal to be displayed when current shift type doesn't match with the type of selected shift template
                setModal({ state: 'closing' });
                changeCurrentDayTemplateIndex(shiftTemplateToClone.type);
                // to prevent OnChange for shiftDuration fields to modify activities' maximumPerDay values
                setCloningState('active');

                // all fields except name, effective date and cloneShiftTemplateId should be re-initialized with values from selected shift template
                form.batch(() => {
                  form.initialize({
                    ...formatValues(shiftTemplateToClone as ShiftTemplate),
                    cloneShiftTemplateId: shiftTemplateIdToClone,
                  });

                  // these changes should be provided separately to not have form in pristine state
                  if (previousName) {
                    form.change('name', previousName);
                  }
                  if (previousEffectiveDate) {
                    form.change('effectiveDate', previousEffectiveDate);
                  }

                  form.change('cloneShiftTemplateId', shiftTemplateIdToClone);
                });

                setCloningState('finishing');
              }
            }}
          </OnChange>

          <ModalConfirmation
            isOpen={modal.state === 'open'}
            title={t('layout.warning')}
            acceptCaption={t('layout.action.proceed')}
            cancelCaption={t('layout.action.cancel')}
            onAccept={handleModalSubmit}
            onCancel={handleModalClose}
          >
            {'newType' in modal &&
              t('shiftTemplate.type.changeModalBody', { type: t(`shiftTemplate.type.${modal.newType}`) })}
          </ModalConfirmation>
        </>
      )}
    </SidebarForm>
  );
};
